import React from 'react';
import Icon from '../../../components/AppIcon';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isToday, startOfWeek, endOfWeek } from 'date-fns';

const CalendarGrid = ({ currentDate, viewMode, events, loading }) => {
  const getEventColor = (event) => {
    const colors = {
      leave: 'bg-blue-100 border-blue-500 text-blue-700',
      events: 'bg-green-100 border-green-500 text-green-700',
      deadlines: 'bg-red-100 border-red-500 text-red-700',
      availability: 'bg-amber-100 border-amber-500 text-amber-700'
    };
    return colors?.[event?.type] || 'bg-gray-100 border-gray-500 text-gray-700';
  };

  const getEventIcon = (event) => {
    const icons = {
      leave: 'Calendar',
      events: 'CalendarCheck',
      deadlines: 'AlertCircle',
      availability: 'Users'
    };
    return icons?.[event?.type] || 'Circle';
  };

  const renderMonthlyView = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const calendarStart = startOfWeek(monthStart);
    const calendarEnd = endOfWeek(monthEnd);
    const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

    const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    return (
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        {/* Header */}
        <div className="grid grid-cols-7 bg-muted border-b border-border">
          {weekDays?.map(day => (
            <div key={day} className="p-3 text-center font-medium text-sm text-foreground">
              {day}
            </div>
          ))}
        </div>
        {/* Calendar Grid */}
        <div className="grid grid-cols-7">
          {days?.map((day, index) => {
            const dayEvents = events?.filter(event => {
              const eventDate = new Date(event.date);
              return isSameDay(eventDate, day);
            });

            const isCurrentMonth = day?.getMonth() === currentDate?.getMonth();
            const isTodayDate = isToday(day);

            return (
              <div
                key={index}
                className={`min-h-[120px] p-2 border-r border-b border-border ${
                  !isCurrentMonth ? 'bg-muted/30' : 'bg-card'
                } ${
                  isTodayDate ? 'bg-primary/5' : ''
                } hover:bg-muted/50 transition-colors cursor-pointer`}
              >
                <div className={`text-sm font-medium mb-1 ${
                  isTodayDate ? 'text-primary font-bold' : isCurrentMonth ? 'text-foreground' : 'text-muted-foreground'
                }`}>
                  {format(day, 'd')}
                </div>
                <div className="space-y-1">
                  {dayEvents?.slice(0, 3)?.map(event => (
                    <div
                      key={event?.id}
                      className={`text-xs p-1 rounded border-l-2 ${getEventColor(event)} truncate`}
                      title={event?.title}
                    >
                      <div className="flex items-center gap-1">
                        <Icon name={getEventIcon(event)} size={10} />
                        <span className="truncate">{event?.title}</span>
                      </div>
                    </div>
                  ))}
                  {dayEvents?.length > 3 && (
                    <div className="text-xs text-muted-foreground pl-1">
                      +{dayEvents?.length - 3} more
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderWeeklyView = () => {
    const weekStart = startOfWeek(currentDate);
    const weekEnd = endOfWeek(currentDate);
    const days = eachDayOfInterval({ start: weekStart, end: weekEnd });

    return (
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="grid grid-cols-7 gap-2 p-4">
          {days?.map((day, index) => {
            const dayEvents = events?.filter(event => isSameDay(new Date(event.date), day));
            const isTodayDate = isToday(day);

            return (
              <div key={index} className={`border border-border rounded-lg p-3 ${
                isTodayDate ? 'bg-primary/5 border-primary' : 'bg-card'
              }`}>
                <div className="text-center mb-3">
                  <div className="text-xs text-muted-foreground">{format(day, 'EEE')}</div>
                  <div className={`text-lg font-bold ${
                    isTodayDate ? 'text-primary' : 'text-foreground'
                  }`}>
                    {format(day, 'd')}
                  </div>
                </div>
                <div className="space-y-2">
                  {dayEvents?.map(event => (
                    <div
                      key={event?.id}
                      className={`text-xs p-2 rounded border-l-2 ${getEventColor(event)}`}
                    >
                      <div className="flex items-center gap-1 mb-1">
                        <Icon name={getEventIcon(event)} size={12} />
                        <span className="font-medium">{event?.title}</span>
                      </div>
                      {event?.employeeName && (
                        <div className="text-xs opacity-75">{event?.employeeName}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderDailyView = () => {
    const dayEvents = events?.filter(event => isSameDay(new Date(event.date), currentDate));

    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="mb-6">
          <h3 className="text-xl font-bold text-foreground mb-1">
            {format(currentDate, 'EEEE, MMMM d, yyyy')}
          </h3>
          <p className="text-sm text-muted-foreground">
            {dayEvents?.length} event{dayEvents?.length !== 1 ? 's' : ''} scheduled
          </p>
        </div>
        <div className="space-y-4">
          {dayEvents?.length === 0 ? (
            <div className="text-center py-12">
              <Icon name="Calendar" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
              <p className="text-muted-foreground">No events scheduled for this day</p>
            </div>
          ) : (
            dayEvents?.map(event => (
              <div
                key={event?.id}
                className={`p-4 rounded-lg border-l-4 ${getEventColor(event)}`}
              >
                <div className="flex items-start gap-3">
                  <Icon name={getEventIcon(event)} size={24} />
                  <div className="flex-1">
                    <h4 className="font-semibold text-base mb-1">{event?.title}</h4>
                    {event?.employeeName && (
                      <p className="text-sm mb-1">Employee: {event?.employeeName}</p>
                    )}
                    {event?.department && event?.department !== 'all' && (
                      <p className="text-sm mb-1">Department: {event?.department?.toUpperCase()}</p>
                    )}
                    {event?.description && (
                      <p className="text-sm opacity-75">{event?.description}</p>
                    )}
                    {event?.status && (
                      <span className="inline-block mt-2 px-2 py-1 text-xs rounded bg-white/50">
                        Status: {event?.status}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-12 text-center">
        <Icon name="Loader" size={48} color="var(--color-primary)" className="mx-auto mb-4 animate-spin" />
        <p className="text-muted-foreground">Loading calendar...</p>
      </div>
    );
  }

  return (
    <div>
      {viewMode === 'monthly' && renderMonthlyView()}
      {viewMode === 'weekly' && renderWeeklyView()}
      {viewMode === 'daily' && renderDailyView()}
    </div>
  );
};

export default CalendarGrid;